import { CONFIG } from '../../config';
import { Particle } from '../../entities/Particle';
import type { Player } from '../../entities/Player';

interface SoundManagerLike {
    playHeartSound(): void;
    playStarSound(): void;
    playCollisionMelody(): void;
}

interface StatsManagerLike {
    recordHeart(): void;
    recordStar(): void;
    recordEvolution(stage: number): void;
    recordHit(): void;
    recordShieldUse(): void;
    recordDevolve(): void;
    recordSurvivedAfterDevolve(): void;
}

interface GameLike {
    getSoundManager(): SoundManagerLike;
    getStatsManager(): StatsManagerLike;
}

/**
 * Particle 엔티티 풀
 */
class ParticleEntityPool {
    private pool: Particle[] = [];
    private maxPoolSize: number = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 50 : 150;

    get(x: number, y: number, vx: number, vy: number, color: string): Particle {
        const particle = this.pool.pop();
        if (particle) {
            particle.reset(x, y, vx, vy, color);
            return particle;
        }
        return new Particle(x, y, vx, vy, color);
    }

    release(particle: Particle): void {
        if (this.pool.length < this.maxPoolSize) {
            this.pool.push(particle);
        }
    }

    clear(): void {
        this.pool = [];
    }
}

/**
 * 플레이어 진화/퇴화 및 피격 처리 담당
 */
export class PlayerStateManager {
    private game: GameLike;
    private player: Player;
    private heartCount: number;
    private heartsToEvolve: number;
    private hitInvincibilityTime: number;
    private hitInvincibilityDuration: number;
    public particles: Particle[];
    private particlePool: ParticleEntityPool;

    constructor(game: GameLike, player: Player) {
        this.game = game;
        this.player = player;

        // 하트 카운터 (2개 모으면 진화)
        this.heartCount = 0;
        this.heartsToEvolve = CONFIG.GAME_SCREEN.HEARTS_TO_EVOLVE;

        // 피격 후 무적 시간
        this.hitInvincibilityTime = 0;
        this.hitInvincibilityDuration = CONFIG.GAME_SCREEN.HIT_INVINCIBILITY_DURATION;

        // 파티클 배열 (외부에서 참조)
        this.particles = [];
        this.particlePool = new ParticleEntityPool();
    }

    reset(): void {
        this.heartCount = 0;
        this.hitInvincibilityTime = 0;
        // 파티클을 풀로 반환
        for (const particle of this.particles) {
            this.particlePool.release(particle);
        }
        this.particles = [];
    }

    update(deltaTime: number): void {
        if (this.hitInvincibilityTime > 0) {
            this.hitInvincibilityTime -= deltaTime;
        }

        // 파티클 업데이트 (swap-and-pop 패턴)
        let writeIdx = 0;
        for (let i = 0; i < this.particles.length; i++) {
            const particle = this.particles[i];
            particle.update(deltaTime);

            if (particle.isAlive) {
                this.particles[writeIdx++] = particle;
            } else {
                this.particlePool.release(particle);
            }
        }
        this.particles.length = writeIdx;
    }

    isInvincible(): boolean {
        return this.hitInvincibilityTime > 0;
    }

    /**
     * 하트 수집
     * @returns 진화했으면 true
     */
    collectHeart(): boolean {
        this.heartCount++;
        this.game.getSoundManager().playHeartSound();

        // Track heart collection for achievements
        this.game.getStatsManager().recordHeart();

        if (this.heartCount >= this.heartsToEvolve && !this.player.isMaxStage()) {
            this.player.evolve();
            this.heartCount = 0;

            // Track evolution for achievements
            this.game.getStatsManager().recordEvolution(this.player.stage);

            return true;
        }
        return false;
    }

    /**
     * 별 수집 (보호막 활성화)
     */
    collectStar(): void {
        this.player.activateShield();
        this.game.getSoundManager().playStarSound();

        // Track star collection for achievements
        this.game.getStatsManager().recordStar();
    }

    /**
     * 플레이어 피격 처리
     * @returns true면 생존, false면 게임오버
     */
    handleHit(): boolean {
        const statsManager = this.game.getStatsManager();

        // Track hit for achievements (resets no-hit streak)
        statsManager.recordHit();

        // 1. 보호막이 있으면 보호막 소모
        if (this.player.useShield()) {
            this.game.getSoundManager().playCollisionMelody();
            this.createExplosion(this.player.x, this.player.y);
            this.hitInvincibilityTime = this.hitInvincibilityDuration;

            // Track shield use for achievements
            statsManager.recordShieldUse();

            return true;
        }

        // 2. 삼각형이 아니면 퇴화
        if (!this.player.isMinStage()) {
            this.player.devolve();
            this.heartCount = 0;
            this.game.getSoundManager().playCollisionMelody();
            this.createExplosion(this.player.x, this.player.y);
            this.hitInvincibilityTime = this.hitInvincibilityDuration;

            // Track devolve for achievements
            statsManager.recordDevolve();
            // Mark that player survived after devolve (for "Second Chance" achievement)
            statsManager.recordSurvivedAfterDevolve();

            return true;
        }

        // 3. 삼각형이면 게임오버
        this.game.getSoundManager().playCollisionMelody();
        this.createExplosion(this.player.x, this.player.y);
        return false;
    }

    createExplosion(x: number, y: number): void {
        const particleCount = CONFIG.GAME_SCREEN.EXPLOSION_PARTICLE_COUNT;
        const color = '#FFFFFF';

        for (let i = 0; i < particleCount; i++) {
            const angle = (Math.PI * 2 * i) / particleCount;
            const speed = 100 + Math.random() * 100;
            const vx = Math.cos(angle) * speed;
            const vy = Math.sin(angle) * speed;

            this.particles.push(this.particlePool.get(x, y, vx, vy, color));
        }
    }

    renderParticles(ctx: CanvasRenderingContext2D): void {
        for (const particle of this.particles) {
            particle.render(ctx);
        }
    }

    getParticles(): Particle[] {
        return this.particles;
    }

    /**
     * 현재 하트 개수 반환
     */
    getHearts(): number {
        return this.heartCount;
    }

    /**
     * 현재 플레이어 스테이지 반환
     */
    getStage(): number {
        return this.player.stage;
    }
}
